import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Fuel as FuelIcon, Plus, Trash2, Edit, X, Loader2, Droplets,
  DollarSign, TrendingUp, FileDown, Package, Gauge, Settings, Search, Eye, BarChart3, RefreshCw
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { authHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from '../components/ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(v) { return `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`; }
function formatDate(d) { if (!d) return '-'; try { const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; } catch { return d; } }

export default function Fuel({ userRole }) {
  const { selectedTenantId, user } = useAuth();
  const canEdit = user?.is_master || userRole === 'admin';
  const [tab, setTab] = useState('stock');
  const [loading, setLoading] = useState(true);
  const [stock, setStock] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [showSupplyForm, setShowSupplyForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editingSupply, setEditingSupply] = useState(null);
  const [config, setConfig] = useState({ tipo_alqueire: 'norte', fator_conversao: 2.72 });
  const [supplyFilters, setSupplyFilters] = useState({ maquina_id: '', date_from: '', date_to: '' });
  const [viewSupply, setViewSupply] = useState(null);
  const [purchaseFilters, setPurchaseFilters] = useState({ date_from: '', date_to: '', tipo_diesel: '', fornecedor_id: '', destino_id: '' });
  const [fornecedores, setFornecedores] = useState([]);
  const [locations, setLocations] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = authHeaders();
      const [stockR, purchR, suppR, machR, opR, confR, fornR, locR] = await Promise.all([
        fetch(`${API_URL}/api/fuel/stock`, { headers: h }),
        fetch(`${API_URL}/api/fuel/purchases`, { headers: h }),
        fetch(`${API_URL}/api/fuel/supplies`, { headers: h }),
        fetch(`${API_URL}/api/machines`, { headers: h }),
        fetch(`${API_URL}/api/operators`, { headers: h }),
        fetch(`${API_URL}/api/company-config`, { headers: h }),
        fetch(`${API_URL}/api/fornecedores`, { headers: h }),
        fetch(`${API_URL}/api/locations`, { headers: h })
      ]);
      if (stockR.ok) setStock(await stockR.json());
      if (purchR.ok) setPurchases(await purchR.json());
      if (suppR.ok) setSupplies(await suppR.json());
      if (machR.ok) setMachines(await machR.json());
      if (opR.ok) setOperators(await opR.json());
      if (confR.ok) setConfig(await confR.json());
      if (fornR.ok) setFornecedores(await fornR.json());
      if (locR.ok) setLocations(await locR.json());
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchSuppliesFiltered = async () => {
    const h = authHeaders();
    const params = new URLSearchParams();
    if (supplyFilters.maquina_id) params.append('maquina_id', supplyFilters.maquina_id);
    if (supplyFilters.date_from) params.append('date_from', supplyFilters.date_from);
    if (supplyFilters.date_to) params.append('date_to', supplyFilters.date_to);
    const r = await fetch(`${API_URL}/api/fuel/supplies?${params}`, { headers: h });
    if (r.ok) setSupplies(await r.json());
  };

  const tabs = [
    { id: 'stock', label: 'Estoque', icon: Package },
    { id: 'purchases', label: 'Compras', icon: DollarSign },
    { id: 'supplies', label: 'Abastecimentos', icon: Droplets },
    { id: 'reports', label: 'Relatórios', icon: TrendingUp },
    { id: 'config', label: 'Configuração', icon: Settings },
  ];

  if (loading && !stock) return (
    <div className="flex items-center justify-center h-64" data-testid="fuel-loading">
      <Loader2 className="animate-spin text-yellow-500" size={40} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="fuel-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
          <FuelIcon className="text-yellow-500" size={28} /> Combustível
        </h1>
        <p className="text-gray-400 mt-1">Controle de diesel S500 — compras, abastecimentos e estoque</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#111] border border-gray-800 rounded-xl p-1 overflow-x-auto" data-testid="fuel-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
              tab === t.id ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid={`fuel-tab-${t.id}`}
          >
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ===== ESTOQUE ===== */}
      {tab === 'stock' && stock && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Saldo em Estoque', value: `${stock.saldo_litros} L`, sub: formatCurrency(stock.saldo_valor), color: 'yellow' },
              { label: 'Reservado (O.S. Abertas)', value: `${stock.total_reservado || 0} L`, sub: `Livre: ${stock.saldo_livre || stock.saldo_litros} L`, color: 'orange' },
              { label: 'Total Comprado', value: `${stock.total_comprado} L`, sub: formatCurrency(stock.total_valor_comprado), color: 'blue' },
              { label: 'Total Abastecido', value: `${stock.total_abastecido} L`, sub: formatCurrency(stock.total_custo_abastecido), color: 'emerald' },
            ].map((c, i) => (
              <div key={i} className={`bg-[#111] border border-${c.color}-500/20 rounded-xl p-4`}>
                <p className={`text-${c.color}-500 text-xs`}>{c.label}</p>
                <p className={`text-${c.color}-500 font-bold text-xl`}>{c.value}</p>
                <p className="text-gray-500 text-xs mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Estoque por Tipo de Diesel */}
          {stock.por_tipo && stock.por_tipo.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {stock.por_tipo.map((t, i) => (
                <div key={i} className={`bg-[#111] border rounded-xl p-3 ${t.tipo === 'S10' ? 'border-cyan-500/20' : 'border-amber-500/20'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs px-2 py-0.5 rounded font-bold ${t.tipo === 'S10' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      Diesel {t.tipo}
                    </span>
                    <span className="text-white font-bold text-sm">{t.disponiveis} L disp.</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Comprado: {t.litros} L | {formatCurrency(t.valor)}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setEditingPurchase(null); setShowPurchaseForm(true); setTab('purchases'); }}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center gap-2"
              data-testid="quick-add-purchase">
              <Plus size={16} /> Nova Compra
            </button>
            <button onClick={() => { setShowSupplyForm(true); setTab('supplies'); }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium text-sm flex items-center gap-2"
              data-testid="quick-add-supply">
              <Droplets size={16} /> Novo Abastecimento
            </button>
            <button onClick={async () => {
              try {
                const r = await fetch(`${API_URL}/api/fuel/recalculate-stock`, { method: 'POST', headers: authHeaders() });
                const d = await r.json();
                if (r.ok) {
                  toast.success(d.message);
                  if (d.corrections?.length > 0) {
                    d.corrections.forEach(c => toast.info(`${c.fornecedor}: ${c.anterior}L → ${c.correto}L (dif: ${c.diferenca > 0 ? '+' : ''}${c.diferenca}L)`));
                  }
                  fetchAll();
                } else toast.error(d.detail || 'Erro');
              } catch { toast.error('Erro de conexao'); }
            }}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium text-sm flex items-center gap-2"
              data-testid="recalculate-stock">
              <RefreshCw size={16} /> Recalcular Estoque
            </button>
          </div>
        </div>
      )}

      {/* ===== COMPRAS ===== */}
      {tab === 'purchases' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">Compras de Diesel</h2>
            <div className="flex gap-2">
              <a href={`${API_URL}/api/fuel/purchases-report/pdf?tenant_id=${selectedTenantId}&issued_by_name=${encodeURIComponent(localStorage.getItem('userName') || 'Responsável')}${purchaseFilters.date_from ? `&date_from=${purchaseFilters.date_from}` : ''}${purchaseFilters.date_to ? `&date_to=${purchaseFilters.date_to}` : ''}${purchaseFilters.tipo_diesel ? `&tipo_diesel=${purchaseFilters.tipo_diesel}` : ''}${purchaseFilters.fornecedor_id ? (purchaseFilters.fornecedor_id.startsWith('text:') ? `&fornecedor=${encodeURIComponent(purchaseFilters.fornecedor_id.replace('text:',''))}` : `&fornecedor_id=${purchaseFilters.fornecedor_id}`) : ''}${purchaseFilters.destino_id ? `&destino_id=${purchaseFilters.destino_id}` : ''}`}
                target="_blank" rel="noopener noreferrer"
                className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium flex items-center gap-1.5"
                data-testid="purchases-export-pdf">
                <FileDown size={14} /> PDF Compras
              </a>
              <button onClick={() => { setEditingPurchase(null); setShowPurchaseForm(true); }}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center gap-2"
                data-testid="add-purchase-btn">
                <Plus size={16} /> Nova Compra
              </button>
            </div>
          </div>

          {/* Filtros de Compras */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Data Inicio</label>
                <input type="date" value={purchaseFilters.date_from} onChange={e => setPurchaseFilters(f => ({...f, date_from: e.target.value}))}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="pf-date-from" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Data Fim</label>
                <input type="date" value={purchaseFilters.date_to} onChange={e => setPurchaseFilters(f => ({...f, date_to: e.target.value}))}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="pf-date-to" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tipo Diesel</label>
                <select value={purchaseFilters.tipo_diesel} onChange={e => setPurchaseFilters(f => ({...f, tipo_diesel: e.target.value}))}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="pf-diesel">
                  <option value="">Todos</option>
                  <option value="S500">S500</option>
                  <option value="S10">S10</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fornecedor</label>
                <select value={purchaseFilters.fornecedor_id} onChange={e => setPurchaseFilters(f => ({...f, fornecedor_id: e.target.value}))}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="pf-fornecedor">
                  <option value="">Todos</option>
                  {(() => {
                    // Unificar: fornecedores da tabela + texto livre de compras antigas
                    const opts = new Map();
                    fornecedores.filter(f => f.ativo).forEach(f => opts.set(f.id, f.nome));
                    purchases.forEach(p => {
                      const nome = p.fornecedor_nome || p.fornecedor;
                      if (nome && p.fornecedor_id && !opts.has(p.fornecedor_id)) {
                        opts.set(p.fornecedor_id, nome);
                      }
                      if (nome && !p.fornecedor_id) {
                        const key = `text:${nome}`;
                        if (!opts.has(key)) opts.set(key, nome);
                      }
                    });
                    return [...opts.entries()]
                      .sort((a, b) => a[1].localeCompare(b[1]))
                      .map(([k, v]) => <option key={k} value={k}>{v}</option>);
                  })()}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Destino</label>
                <select value={purchaseFilters.destino_id} onChange={e => setPurchaseFilters(f => ({...f, destino_id: e.target.value}))}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="pf-destino">
                  <option value="">Todos</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.full_name}</option>)}
                </select>
              </div>
              {(purchaseFilters.date_from || purchaseFilters.date_to || purchaseFilters.tipo_diesel || purchaseFilters.fornecedor_id || purchaseFilters.destino_id) && (
                <button onClick={() => setPurchaseFilters({ date_from: '', date_to: '', tipo_diesel: '', fornecedor_id: '', destino_id: '' })}
                  className="px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-lg hover:text-white" data-testid="pf-clear">Limpar</button>
              )}
            </div>
          </div>

          {showPurchaseForm && <PurchaseForm purchase={editingPurchase} onClose={() => { setShowPurchaseForm(false); setEditingPurchase(null); }} onSaved={() => { setShowPurchaseForm(false); setEditingPurchase(null); fetchAll(); }} />}

          {(() => {
            const filtered = purchases.filter(p => {
              if (purchaseFilters.date_from && p.data < purchaseFilters.date_from) return false;
              if (purchaseFilters.date_to && p.data > purchaseFilters.date_to) return false;
              if (purchaseFilters.tipo_diesel && (p.tipo_diesel || 'S500') !== purchaseFilters.tipo_diesel) return false;
              if (purchaseFilters.fornecedor_id) {
                const fv = purchaseFilters.fornecedor_id;
                if (fv.startsWith('text:')) {
                  // Filtrar por texto livre (compras antigas sem fornecedor_id)
                  const texto = fv.replace('text:', '');
                  const nome = (p.fornecedor_nome || p.fornecedor || '').toLowerCase();
                  if (nome !== texto.toLowerCase()) return false;
                } else {
                  // Filtrar por ID da tabela de fornecedores
                  if (p.fornecedor_id !== fv) return false;
                }
              }
              if (purchaseFilters.destino_id && p.destino_id !== purchaseFilters.destino_id) return false;
              return true;
            });
            const totLitros = filtered.reduce((s, p) => s + p.litros, 0);
            const totDisp = filtered.reduce((s, p) => s + p.litros_disponiveis, 0);
            const totValor = filtered.reduce((s, p) => s + p.valor_total, 0);
            return (
              <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full" data-testid="purchases-table">
                    <thead className="bg-gray-900/50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fornecedor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Destino</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Diesel</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Litros</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Disponivel</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">R$/Litro</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Acoes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filtered.length > 0 ? filtered.map(p => (
                        <tr key={p.id} className="hover:bg-gray-900/30">
                          <td className="px-4 py-3 text-sm text-white">{formatDate(p.data)}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{p.fornecedor_nome || p.fornecedor}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{p.destino_nome || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${(p.tipo_diesel || 'S500') === 'S10' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {p.tipo_diesel || 'S500'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-white text-right font-mono">{p.litros} L</td>
                          <td className="px-4 py-3 text-sm text-right font-mono">
                            <span className={p.litros_disponiveis > 0 ? 'text-emerald-400' : 'text-red-400'}>{p.litros_disponiveis} L</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300 text-right font-mono">{formatCurrency(p.valor_unitario)}</td>
                          <td className="px-4 py-3 text-sm text-yellow-500 text-right font-mono font-semibold">{formatCurrency(p.valor_total)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-1 rounded ${p.responsabilidade === 'empresa' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                              {p.responsabilidade === 'empresa' ? 'Empresa' : 'Contratante'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => { setEditingPurchase(p); setShowPurchaseForm(true); }} className="p-1.5 text-gray-400 hover:text-yellow-500" data-testid={`edit-purchase-${p.id}`}><Edit size={14} /></button>
                              <button onClick={async () => {
                                if (!window.confirm('Excluir esta compra?')) return;
                                const r = await fetch(`${API_URL}/api/fuel/purchases/${p.id}`, { method: 'DELETE', headers: authHeaders() });
                                if (r.ok) { toast.success('Compra excluida'); fetchAll(); } else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
                              }} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-purchase-${p.id}`}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">Nenhuma compra encontrada</td></tr>
                      )}
                    </tbody>
                    {filtered.length > 0 && (
                      <tfoot className="bg-gray-900/70 border-t border-gray-700">
                        <tr>
                          <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-yellow-500 text-right">TOTAIS ({filtered.length} compras)</td>
                          <td className="px-4 py-3 text-sm text-white text-right font-mono font-bold">{totLitros.toFixed(1)} L</td>
                          <td className="px-4 py-3 text-sm text-right font-mono font-bold">
                            <span className={totDisp > 0 ? 'text-emerald-400' : 'text-red-400'}>{totDisp.toFixed(1)} L</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400 text-right font-mono">-</td>
                          <td className="px-4 py-3 text-sm text-yellow-500 text-right font-mono font-bold">{formatCurrency(totValor)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== ABASTECIMENTOS ===== */}
      {tab === 'supplies' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h2 className="text-lg font-semibold text-white">O.S. de Abastecimento</h2>
            <button onClick={() => setShowSupplyForm(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium text-sm flex items-center gap-2"
              data-testid="add-supply-btn">
              <Plus size={16} /> Novo Abastecimento
            </button>
          </div>

          {/* Filtros */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <select value={supplyFilters.maquina_id} onChange={e => setSupplyFilters({ ...supplyFilters, maquina_id: e.target.value })}
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-filter-machine">
                <option value="">Todas as máquinas</option>
                {machines.filter(m => m.status === 'active').map(m => <option key={m.id} value={m.id}>{m.serial_number} - {m.brand} {m.model}</option>)}
              </select>
              <input type="date" value={supplyFilters.date_from} onChange={e => setSupplyFilters({ ...supplyFilters, date_from: e.target.value })}
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-filter-from" />
              <input type="date" value={supplyFilters.date_to} onChange={e => setSupplyFilters({ ...supplyFilters, date_to: e.target.value })}
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-filter-to" />
              <button onClick={fetchSuppliesFiltered} className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center justify-center gap-2" data-testid="supply-filter-btn">
                <Search size={15} /> Filtrar
              </button>
            </div>
          </div>

          {(showSupplyForm || editingSupply) && <SupplyForm machines={machines} operators={operators} purchases={purchases.filter(p => p.litros_disponiveis > 0 || (editingSupply && p.id === editingSupply.compra_id))} supply={editingSupply} onClose={() => { setShowSupplyForm(false); setEditingSupply(null); }} onSaved={() => { setShowSupplyForm(false); setEditingSupply(null); fetchAll(); }} />}

          {viewSupply && <SupplyDetail supply={viewSupply} onClose={() => setViewSupply(null)} />}

          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full" data-testid="supplies-table">
                <thead className="bg-gray-900/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Máquina</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Operador</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">Horímetro</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">Litros</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">Valor Total (R$)</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">Fotos</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {supplies.length > 0 ? supplies.map(s => (
                    <tr key={s.id} className="hover:bg-gray-900/30">
                      <td className="px-3 py-3 text-sm text-white">{formatDate(s.data)} {s.hora || ''}</td>
                      <td className="px-3 py-3 text-sm text-gray-300">{s.machine_info ? s.machine_info.serial_number || `${s.machine_info.brand} ${s.machine_info.model}` : '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-300">{s.operator_info?.name || '-'}</td>
                      <td className="px-3 py-3 text-sm text-white text-right font-mono">{s.horimetro != null ? s.horimetro : '-'}</td>
                      <td className="px-3 py-3 text-sm text-white text-right font-mono font-semibold">{s.litros_abastecidos} L</td>
                      <td className="px-3 py-3 text-sm text-yellow-500 text-right font-mono font-semibold">{formatCurrency(s.custo_calculado)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs ${s.fotos_bomba?.length > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {s.fotos_bomba?.length || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded ${s.status === 'closed' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                          {s.status === 'closed' ? 'Fechada' : 'Aberta'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setViewSupply(s)} className="p-1.5 text-gray-400 hover:text-yellow-500" data-testid={`view-supply-${s.id}`}><Eye size={14} /></button>
                          {canEdit && (
                            <button onClick={() => { setEditingSupply(s); setShowSupplyForm(false); }} className="p-1.5 text-gray-400 hover:text-blue-400" data-testid={`edit-supply-${s.id}`}><Edit size={14} /></button>
                          )}
                          {s.status === 'open' && (
                            <button onClick={async () => {
                              if (!s.fotos_bomba?.length) { toast.error('Adicione fotos da bomba antes de fechar'); return; }
                              const r = await fetch(`${API_URL}/api/fuel/supplies/${s.id}/close`, { method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ fotos_bomba: [] }) });
                              if (r.ok) { toast.success('O.S. fechada'); fetchAll(); } else { const d = await r.json(); toast.error(d.detail); }
                            }} className="px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded" data-testid={`close-supply-${s.id}`}>Fechar</button>
                          )}
                          <button onClick={async () => {
                            if (!window.confirm('Excluir e restaurar estoque?')) return;
                            const r = await fetch(`${API_URL}/api/fuel/supplies/${s.id}`, { method: 'DELETE', headers: authHeaders() });
                            if (r.ok) { toast.success('O.S. excluída, estoque restaurado'); fetchAll(); } else { const d = await r.json(); toast.error(d.detail); }
                          }} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-supply-${s.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Nenhum abastecimento registrado</td></tr>
                  )}
                  {supplies.length > 0 && (
                    <tr className="bg-gray-900/60 border-t-2 border-yellow-500/30">
                      <td colSpan={4} className="px-3 py-3 text-sm text-yellow-500 font-bold text-right">TOTAL</td>
                      <td className="px-3 py-3 text-sm text-emerald-400 text-right font-mono font-bold">{supplies.reduce((a, s) => a + s.litros_abastecidos, 0).toFixed(2)} L</td>
                      <td className="px-3 py-3 text-sm text-yellow-500 text-right font-mono font-bold">{formatCurrency(supplies.reduce((a, s) => a + s.custo_calculado, 0))}</td>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== RELATÓRIOS ===== */}
      {tab === 'reports' && (
        <FuelReports selectedTenantId={selectedTenantId} machines={machines} />
      )}

      {/* ===== CONFIGURAÇÃO ===== */}
      {tab === 'config' && (
        <ConfigPanel config={config} onSaved={fetchAll} />
      )}
    </div>
  );
}

// ====== FORMULÁRIO DE COMPRA ======
function PurchaseForm({ purchase, onClose, onSaved }) {
  const defaultForm = {
    data: new Date().toISOString().split('T')[0],
    fornecedor_id: '',
    destino_id: '',
    tipo_diesel: 'S500',
    litros: '',
    valor_unitario: '',
    responsabilidade: 'empresa',
    nota_fiscal: '',
    observacao: ''
  };
  const [form, setForm] = useState(purchase ? {
    data: purchase.data,
    fornecedor_id: purchase.fornecedor_id || '',
    destino_id: purchase.destino_id || '',
    tipo_diesel: purchase.tipo_diesel || 'S500',
    litros: purchase.litros,
    valor_unitario: purchase.valor_unitario,
    responsabilidade: purchase.responsabilidade,
    nota_fiscal: purchase.nota_fiscal || '',
    observacao: purchase.observacao || ''
  } : { ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showNewFornecedor, setShowNewFornecedor] = useState(false);
  const [newFornNome, setNewFornNome] = useState('');
  const [newFornTipo, setNewFornTipo] = useState('Externo');
  const [savingForn, setSavingForn] = useState(false);

  useEffect(() => {
    const h = authHeaders();
    Promise.all([
      fetch(`${API_URL}/api/fornecedores`, { headers: h }),
      fetch(`${API_URL}/api/locations`, { headers: h })
    ]).then(async ([fR, lR]) => {
      if (fR.ok) setFornecedores(await fR.json());
      if (lR.ok) setLocations(await lR.json());
    });
  }, []);

  const valorTotal = form.litros && form.valor_unitario ? (parseFloat(form.litros) * parseFloat(form.valor_unitario)).toFixed(2) : '0.00';

  const handleCreateFornecedor = async () => {
    if (!newFornNome.trim()) { toast.error('Preencha o nome do fornecedor'); return; }
    setSavingForn(true);
    try {
      const r = await fetch(`${API_URL}/api/fornecedores`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ nome: newFornNome.trim(), tipo: newFornTipo })
      });
      if (r.ok) {
        const created = await r.json();
        // Refresh list and select the new one
        const listR = await fetch(`${API_URL}/api/fornecedores`, { headers: authHeaders() });
        if (listR.ok) setFornecedores(await listR.json());
        setForm(prev => ({ ...prev, fornecedor_id: created.id }));
        setShowNewFornecedor(false);
        setNewFornNome('');
        toast.success('Fornecedor cadastrado');
      } else { const d = await r.json(); toast.error(d.detail || 'Erro ao cadastrar'); }
    } catch { toast.error('Erro de conexao'); }
    finally { setSavingForn(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fornecedor_id || !form.litros || !form.valor_unitario) { toast.error('Preencha fornecedor, litros e valor/litro'); return; }
    if (!form.destino_id) { toast.error('Selecione o destino/operacao'); return; }
    setSaving(true);
    try {
      const url = purchase ? `${API_URL}/api/fuel/purchases/${purchase.id}` : `${API_URL}/api/fuel/purchases`;
      const r = await fetch(url, {
        method: purchase ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...form,
          litros: parseFloat(form.litros),
          valor_unitario: parseFloat(form.valor_unitario),
          valor_total: parseFloat(valorTotal),
          tipo_diesel: form.tipo_diesel
        })
      });
      if (r.ok) {
        toast.success(purchase ? 'Compra atualizada' : 'Compra registrada');
        if (!purchase) setForm({ ...defaultForm });
        onSaved();
      }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexao'); }
    finally { setSaving(false); }
  };

  const fornLocais = fornecedores.filter(f => f.tipo === 'Local' && f.ativo);
  const fornExternos = fornecedores.filter(f => f.tipo !== 'Local' && f.ativo);

  return (
    <div className="bg-[#111] border border-yellow-500/30 rounded-xl p-5" data-testid="purchase-form">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-semibold">{purchase ? 'Editar Compra' : 'Nova Compra de Diesel'}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Data</label>
          <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-date" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Fornecedor *</label>
          <select value={form.fornecedor_id} onChange={e => {
            if (e.target.value === '__new__') { setShowNewFornecedor(true); }
            else { setForm({ ...form, fornecedor_id: e.target.value }); setShowNewFornecedor(false); }
          }}
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-fornecedor">
            <option value="">Selecione o fornecedor</option>
            {fornLocais.length > 0 && <optgroup label="Locais de Operacao">
              {fornLocais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </optgroup>}
            {fornExternos.length > 0 && <optgroup label="Fornecedores Externos">
              {fornExternos.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </optgroup>}
            <option value="__new__">+ Cadastrar Novo Fornecedor</option>
          </select>
          {showNewFornecedor && (
            <div className="mt-2 p-3 bg-[#0A0A0A] border border-yellow-500/30 rounded-lg space-y-2" data-testid="new-fornecedor-inline">
              <input type="text" value={newFornNome} onChange={e => setNewFornNome(e.target.value)}
                placeholder="Nome do fornecedor" className="w-full px-3 py-1.5 bg-[#111] border border-gray-700 rounded text-white text-sm" data-testid="new-forn-nome" />
              <select value={newFornTipo} onChange={e => setNewFornTipo(e.target.value)}
                className="w-full px-3 py-1.5 bg-[#111] border border-gray-700 rounded text-white text-sm" data-testid="new-forn-tipo">
                <option value="Externo">Fornecedor Externo</option>
                <option value="Local">Local de Operacao</option>
              </select>
              <div className="flex gap-2">
                <button type="button" onClick={handleCreateFornecedor} disabled={savingForn}
                  className="px-3 py-1.5 bg-yellow-500 text-black text-xs font-semibold rounded hover:bg-yellow-400 disabled:opacity-50" data-testid="new-forn-save">
                  {savingForn ? 'Salvando...' : 'Cadastrar'}
                </button>
                <button type="button" onClick={() => { setShowNewFornecedor(false); setForm(prev => ({ ...prev, fornecedor_id: '' })); }}
                  className="px-3 py-1.5 border border-gray-700 text-gray-400 text-xs rounded">Cancelar</button>
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Destino / Operacao *</label>
          <select value={form.destino_id} onChange={e => setForm({ ...form, destino_id: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-destino">
            <option value="">Selecione o destino</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Tipo de Diesel *</label>
          <select value={form.tipo_diesel} onChange={e => setForm({ ...form, tipo_diesel: e.target.value })}
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-tipo-diesel">
            <option value="S500">Diesel S500</option>
            <option value="S10">Diesel S10</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Quantidade (Litros) *</label>
          <input type="number" step="0.01" value={form.litros} onChange={e => setForm({ ...form, litros: e.target.value })} required placeholder="0.00"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-litros" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Valor por Litro (R$) *</label>
          <input type="number" step="0.01" value={form.valor_unitario} onChange={e => setForm({ ...form, valor_unitario: e.target.value })} required placeholder="0.00"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-valor" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Valor Total (R$)</label>
          <div className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-yellow-500 font-bold text-sm">{formatCurrency(valorTotal)}</div>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Responsabilidade</label>
          <select value={form.responsabilidade} onChange={e => setForm({ ...form, responsabilidade: e.target.value })}
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-responsabilidade">
            <option value="empresa">Por conta da Empresa</option>
            <option value="contratante">Fornecido pelo Contratante</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Nota Fiscal</label>
          <input type="text" value={form.nota_fiscal} onChange={e => setForm({ ...form, nota_fiscal: e.target.value })} placeholder="Número NF (opcional)"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="purchase-nf" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-400 mb-1">Observação</label>
          <input type="text" value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })} placeholder="Observação (opcional)"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </div>
        <div className="md:col-span-2 lg:col-span-3 flex gap-3">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 text-black font-semibold rounded-lg text-sm flex items-center gap-2" data-testid="purchase-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {purchase ? 'Atualizar' : 'Registrar Compra'}
          </button>
          <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

// ====== FORMULÁRIO DE ABASTECIMENTO ======
function SupplyForm({ machines, operators, purchases, supply, onClose, onSaved }) {
  const isEdit = !!supply;
  const defaultForm = {
    maquina_id: '', operador_id: '', data: new Date().toISOString().split('T')[0], hora: '',
    horimetro: '', leitura_inicial_bomba: '', leitura_final_bomba: '', compra_id: '', observacao: ''
  };
  const [form, setForm] = useState(isEdit ? {
    maquina_id: supply.maquina_id,
    operador_id: supply.operador_id,
    data: supply.data,
    hora: supply.hora || '',
    horimetro: supply.horimetro != null ? String(supply.horimetro) : '',
    leitura_inicial_bomba: String(supply.leitura_inicial_bomba),
    leitura_final_bomba: String(supply.leitura_final_bomba),
    compra_id: supply.compra_id,
    observacao: supply.observacao || ''
  } : { ...defaultForm });
  const [fotos, setFotos] = useState(isEdit
    ? [supply.fotos_bomba?.[0] || null, supply.fotos_bomba?.[1] || null]
    : [null, null]);
  const [saving, setSaving] = useState(false);

  const litrosCalc = form.leitura_final_bomba && form.leitura_inicial_bomba
    ? Math.max(0, parseFloat(form.leitura_final_bomba) - parseFloat(form.leitura_inicial_bomba)).toFixed(2) : '0.00';

  const compraSelecionada = purchases.find(p => p.id === form.compra_id);
  const custoCalc = compraSelecionada ? (parseFloat(litrosCalc) * compraSelecionada.valor_unitario).toFixed(2) : '0.00';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.maquina_id || !form.operador_id || !form.compra_id) { toast.error('Selecione máquina, operador e lote de compra'); return; }
    if (!form.horimetro) { toast.error('Informe a leitura do horímetro/odômetro'); return; }
    if (!form.leitura_inicial_bomba || !form.leitura_final_bomba) { toast.error('Informe as leituras da bomba'); return; }
    const validFotos = fotos.filter(f => f);
    if (validFotos.length === 0) { toast.error('Foto da bomba de combustível é obrigatória'); return; }
    
    setSaving(true);
    try {
      const url = isEdit ? `${API_URL}/api/fuel/supplies/${supply.id}` : `${API_URL}/api/fuel/supplies`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...form,
          horimetro: parseFloat(form.horimetro),
          leitura_inicial_bomba: parseFloat(form.leitura_inicial_bomba),
          leitura_final_bomba: parseFloat(form.leitura_final_bomba),
          fotos_bomba: validFotos
        })
      });
      if (r.ok) {
        toast.success(isEdit ? 'Abastecimento atualizado' : `Abastecimento registrado: ${litrosCalc}L`);
        if (!isEdit) { setForm({ ...defaultForm }); setFotos([null, null]); }
        onSaved();
      }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111] border border-emerald-500/30 rounded-xl p-5" data-testid="supply-form">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-semibold">{isEdit ? 'Editar Abastecimento' : 'Nova O.S. de Abastecimento'}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Máquina *</label>
          <select value={form.maquina_id} onChange={e => setForm({ ...form, maquina_id: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-machine">
            <option value="">Selecione</option>
            {machines.filter(m => m.status === 'active').map(m => <option key={m.id} value={m.id}>{m.serial_number} - {m.brand} {m.model}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Operador *</label>
          <select value={form.operador_id} onChange={e => setForm({ ...form, operador_id: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-operator">
            <option value="">Selecione</option>
            {operators.filter(o => o.status === 'active').map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Data *</label>
          <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-date" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Hora</label>
          <input type="time" value={form.hora} onChange={e => setForm({ ...form, hora: e.target.value })}
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-hora" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Horímetro/Odômetro *</label>
          <input type="number" step="0.01" value={form.horimetro} onChange={e => setForm({ ...form, horimetro: e.target.value })} required placeholder="Leitura atual"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-horimetro" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Leitura Inicial Bomba (Litros) *</label>
          <input type="number" step="0.01" value={form.leitura_inicial_bomba} onChange={e => setForm({ ...form, leitura_inicial_bomba: e.target.value })} required placeholder="0.00"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-leitura-ini" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Leitura Final Bomba (Litros) *</label>
          <input type="number" step="0.01" value={form.leitura_final_bomba} onChange={e => setForm({ ...form, leitura_final_bomba: e.target.value })} required placeholder="0.00"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-leitura-fim" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Lote de Compra *</label>
          <select value={form.compra_id} onChange={e => setForm({ ...form, compra_id: e.target.value })} required
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="supply-compra">
            <option value="">Selecione o lote</option>
            {purchases.map(p => <option key={p.id} value={p.id}>{formatDate(p.data)} - {p.fornecedor} [{p.tipo_diesel || 'S500'}] ({p.litros_disponiveis}L disp. | {formatCurrency(p.valor_unitario)}/L)</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Litros Calculados</label>
          <div className={`px-3 py-2 bg-gray-900 border rounded-lg font-bold text-sm font-mono ${
            compraSelecionada && parseFloat(litrosCalc) > compraSelecionada.litros_disponiveis
              ? 'border-red-500 text-red-400' : 'border-gray-700 text-emerald-400'
          }`}>{litrosCalc} L</div>
          {compraSelecionada && parseFloat(litrosCalc) > compraSelecionada.litros_disponiveis && (
            <p className="text-red-400 text-xs mt-1">Excede o disponivel no lote ({compraSelecionada.litros_disponiveis}L)</p>
          )}
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Custo Calculado (R$)</label>
          <div className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-yellow-500 font-bold text-sm">{formatCurrency(custoCalc)}</div>
        </div>

        {/* Fotos da Bomba */}
        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-sm text-gray-400 mb-2">Fotos da Bomba de Combustível * <span className="text-red-400">(obrigatório)</span></label>
          <div className="grid grid-cols-2 gap-4">
            <ImageUpload value={fotos[0]} onChange={v => { const nf = [...fotos]; nf[0] = v; setFotos(nf); }} label="Foto Inicial (bomba)" folder="combustivel" />
            <ImageUpload value={fotos[1]} onChange={v => { const nf = [...fotos]; nf[1] = v; setFotos(nf); }} label="Foto Final (bomba)" folder="combustivel" />
          </div>
        </div>

        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-sm text-gray-400 mb-1">Observação</label>
          <input type="text" value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })} placeholder="Observação (opcional)"
            className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </div>

        <div className="md:col-span-2 lg:col-span-3 flex gap-3">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white font-semibold rounded-lg text-sm flex items-center gap-2" data-testid="supply-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Droplets size={16} />}
            {isEdit ? 'Atualizar Abastecimento' : 'Registrar Abastecimento'}
          </button>
          <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

// ====== DETALHE DO ABASTECIMENTO ======
function SupplyDetail({ supply, onClose }) {
  return (
    <div className="bg-[#111] border border-gray-700 rounded-xl p-5" data-testid="supply-detail">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-semibold">Detalhe do Abastecimento</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 text-sm">
        <div><span className="text-gray-500">Data:</span> <span className="text-white ml-2">{formatDate(supply.data)} {supply.hora || ''}</span></div>
        <div><span className="text-gray-500">Máquina:</span> <span className="text-white ml-2">{supply.machine_info ? supply.machine_info.serial_number || `${supply.machine_info.brand} ${supply.machine_info.model}` : '-'}</span></div>
        <div><span className="text-gray-500">Operador:</span> <span className="text-white ml-2">{supply.operator_info?.name || '-'}</span></div>
        <div><span className="text-gray-500">Horímetro:</span> <span className="text-white ml-2">{supply.horimetro != null ? supply.horimetro : '-'}</span></div>
        <div><span className="text-gray-500">Leitura Bomba:</span> <span className="text-white ml-2">{supply.leitura_inicial_bomba} → {supply.leitura_final_bomba}</span></div>
        <div><span className="text-gray-500">Litros:</span> <span className="text-emerald-400 font-bold ml-2">{supply.litros_abastecidos} L</span></div>
        <div><span className="text-gray-500">Custo:</span> <span className="text-yellow-500 font-bold ml-2">{formatCurrency(supply.custo_calculado)}</span></div>
        <div><span className="text-gray-500">Lote:</span> <span className="text-white ml-2">{supply.compra_info?.fornecedor || '-'} ({formatCurrency(supply.compra_info?.valor_unitario)}/L)</span></div>
        <div><span className="text-gray-500">Status:</span> <span className={`ml-2 font-bold ${supply.status === 'closed' ? 'text-green-400' : 'text-orange-400'}`}>{supply.status === 'closed' ? 'Fechada' : 'Aberta'}</span></div>
      </div>
      {supply.fotos_bomba?.length > 0 && (
        <div className="mt-4">
          <p className="text-gray-400 text-sm mb-2">Fotos da Bomba:</p>
          <div className="grid grid-cols-2 gap-3">
            {supply.fotos_bomba.map((f, i) => (
              <img key={i} src={f} alt={`Bomba ${i + 1}`} className="rounded-lg border border-gray-700 h-40 w-full object-cover" />
            ))}
          </div>
        </div>
      )}
      {/* Log de Auditoria */}
      {supply.audit_log && supply.audit_log.length > 0 && (
        <div className="mt-4">
          <p className="text-gray-400 text-sm mb-2 font-semibold">Histórico de Alterações:</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {supply.audit_log.slice().reverse().map((log, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-lg p-2 text-xs">
                <div className="flex justify-between text-gray-500">
                  <span>{log.usuario}</span>
                  <span>{log.data_hora ? new Date(log.data_hora).toLocaleString('pt-BR') : ''}</span>
                </div>
                <p className="text-gray-300 mt-1">
                  <span className="text-yellow-500 font-medium">{log.campo}</span>: {log.valor_anterior} → <span className="text-emerald-400">{log.valor_novo}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ====== RELATÓRIOS DE COMBUSTÍVEL ======
function FuelReports({ selectedTenantId, machines }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', maquina_id: '' });

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.maquina_id) params.append('maquina_id', filters.maquina_id);
      const r = await fetch(`${API_URL}/api/fuel/report?${params}`, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setReportData(data);
        toast.success(`Relatório: ${data.by_machine.length} máquinas`);
      }
    } catch { toast.error('Erro'); }
    finally { setLoading(false); }
  };

  const getPDFUrl = (pdfMode = 'resumo') => {
    const params = new URLSearchParams();
    params.append('tenant_id', selectedTenantId);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.maquina_id) params.append('maquina_id', filters.maquina_id);
    params.append('mode', pdfMode);
    const userName = localStorage.getItem('userName') || 'Responsável';
    params.append('issued_by_name', userName);
    return `${API_URL}/api/fuel/report-pdf?${params}`;
  };

  const totalLitros = reportData ? reportData.by_machine.reduce((a, m) => a + m.litros, 0) : 0;
  const totalCusto = reportData ? reportData.by_machine.reduce((a, m) => a + m.custo, 0) : 0;
  const totalAbast = reportData ? reportData.by_machine.reduce((a, m) => a + m.abastecimentos, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Gauge size={18} className="text-yellow-500" /> Relatório de Combustível por Máquina</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select value={filters.maquina_id} onChange={e => setFilters({ ...filters, maquina_id: e.target.value })}
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="fuel-report-machine">
            <option value="">Todas as máquinas</option>
            {(machines || []).filter(m => m.status === 'active').map(m => <option key={m.id} value={m.id}>{m.serial_number} - {m.brand} {m.model}</option>)}
          </select>
          <input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })}
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="fuel-report-from" />
          <input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })}
            className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="fuel-report-to" />
          <button onClick={fetchReport} disabled={loading}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center justify-center gap-2" data-testid="fuel-report-btn">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <TrendingUp size={15} />} Gerar Relatório
          </button>
        </div>
      </div>

      {reportData && (
        <>
          {/* Dashboard Charts */}
          {reportData.by_machine.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bar Chart - Consumo por Máquina */}
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 size={16} className="text-emerald-400" /> Litros por Máquina
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={reportData.by_machine} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="machine_name" tick={{ fill: '#9ca3af', fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                      formatter={(value, name) => [name === 'litros' ? `${value} L` : formatCurrency(value), name === 'litros' ? 'Litros' : 'Custo']} />
                    <Bar dataKey="litros" fill="#34d399" radius={[4, 4, 0, 0]} name="litros" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart - Custo por Máquina */}
              <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <DollarSign size={16} className="text-yellow-400" /> Custo por Máquina (R$)
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={reportData.by_machine} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="machine_name" tick={{ fill: '#9ca3af', fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                      formatter={(value) => [formatCurrency(value), 'Custo']} />
                    <Bar dataKey="custo" fill="#eab308" radius={[4, 4, 0, 0]} name="custo" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Line Chart - Tendência de consumo ao longo do tempo */}
          {reportData.daily && reportData.daily.length > 1 && (
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-blue-400" /> Tendência de Consumo Diário
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={reportData.daily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="data" tick={{ fill: '#9ca3af', fontSize: 11 }}
                    tickFormatter={(v) => { try { const [, m, d] = v.split('-'); return `${d}/${m}`; } catch { return v; } }} />
                  <YAxis yAxisId="left" tick={{ fill: '#34d399', fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#eab308', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                    labelFormatter={(v) => formatDate(v)}
                    formatter={(value, name) => [name === 'litros' ? `${value} L` : formatCurrency(value), name === 'litros' ? 'Litros' : 'Custo']} />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Line yAxisId="left" type="monotone" dataKey="litros" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} name="litros" />
                  <Line yAxisId="right" type="monotone" dataKey="custo" stroke="#eab308" strokeWidth={2} dot={{ r: 3 }} name="custo" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center">
            <h3 className="text-white font-semibold">Consumo por Máquina</h3>
            <div className="flex gap-2">
              <button onClick={() => { const l = document.createElement('a'); l.href = getPDFUrl('resumo'); l.target = '_blank'; document.body.appendChild(l); l.click(); document.body.removeChild(l); toast.success('Gerando PDF resumo...'); }}
                className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium flex items-center gap-1.5" data-testid="fuel-export-pdf">
                <FileDown size={14} /> PDF Resumo
              </button>
              <button onClick={() => { const l = document.createElement('a'); l.href = getPDFUrl('detalhado'); l.target = '_blank'; document.body.appendChild(l); l.click(); document.body.removeChild(l); toast.success('Gerando PDF detalhado...'); }}
                className="px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-medium flex items-center gap-1.5" data-testid="fuel-export-pdf-detail">
                <FileDown size={14} /> PDF Detalhado
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="fuel-report-table">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Máquina</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Abastecimentos</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Litros</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Valor Total (R$)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Média L/Abast.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {reportData.by_machine.map((m, i) => (
                  <tr key={i} className="hover:bg-gray-900/30">
                    <td className="px-4 py-3 text-sm text-white font-medium">{m.machine_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right">{m.abastecimentos}</td>
                    <td className="px-4 py-3 text-sm text-emerald-400 text-right font-mono font-semibold">{m.litros} L</td>
                    <td className="px-4 py-3 text-sm text-yellow-500 text-right font-mono font-semibold">{formatCurrency(m.custo)}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right font-mono">{m.abastecimentos > 0 ? (m.litros / m.abastecimentos).toFixed(1) : '-'} L</td>
                  </tr>
                ))}
                {reportData.by_machine.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Nenhum dado no período</td></tr>
                )}
                {reportData.by_machine.length > 0 && (
                  <tr className="bg-gray-900/60 border-t-2 border-yellow-500/30">
                    <td className="px-4 py-3 text-sm text-yellow-500 font-bold">TOTAL</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right font-bold">{totalAbast}</td>
                    <td className="px-4 py-3 text-sm text-emerald-400 text-right font-mono font-bold">{totalLitros.toFixed(2)} L</td>
                    <td className="px-4 py-3 text-sm text-yellow-500 text-right font-mono font-bold">{formatCurrency(totalCusto)}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right font-mono font-bold">{totalAbast > 0 ? (totalLitros / totalAbast).toFixed(1) : '-'} L</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
}

// ====== CONFIGURAÇÃO (ALQUEIRE) ======
function ConfigPanel({ config, onSaved }) {
  const [form, setForm] = useState({
    tipo_alqueire: config.tipo_alqueire || 'norte',
    fator_conversao: config.fator_conversao || 2.72
  });
  const [saving, setSaving] = useState(false);

  const presets = { norte: 2.72, paulista: 2.42, mineiro: 4.84 };

  const handleTipoChange = (tipo) => {
    if (tipo === 'personalizado') {
      setForm({ tipo_alqueire: tipo, fator_conversao: form.fator_conversao });
    } else {
      setForm({ tipo_alqueire: tipo, fator_conversao: presets[tipo] });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/company-config`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      if (r.ok) { toast.success('Configuração salva'); onSaved(); }
      else toast.error('Erro ao salvar');
    } catch { toast.error('Erro de conexão'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-xl p-5" data-testid="config-panel">
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
        <Settings size={18} className="text-yellow-500" /> Fator de Conversão Hectare x Alqueire
      </h3>
      <p className="text-gray-400 text-sm mb-4">Defina o tipo de alqueire utilizado na região de operação. Esse fator é aplicado automaticamente nos relatórios de produção e equiparação.</p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {[
          { id: 'norte', label: 'Alqueire do Norte', sub: '1 alq = 2.72 ha', region: 'Pará, Goiás, MT' },
          { id: 'paulista', label: 'Alqueire Paulista', sub: '1 alq = 2.42 ha', region: 'São Paulo' },
          { id: 'mineiro', label: 'Alqueire Mineiro', sub: '1 alq = 4.84 ha', region: 'Minas Gerais' },
          { id: 'personalizado', label: 'Personalizado', sub: 'Valor livre', region: 'Definido pela empresa' },
        ].map(opt => (
          <button key={opt.id} onClick={() => handleTipoChange(opt.id)}
            className={`p-4 rounded-xl border text-left transition-all ${
              form.tipo_alqueire === opt.id
                ? 'border-yellow-500 bg-yellow-500/10'
                : 'border-gray-800 bg-[#0A0A0A] hover:border-gray-600'
            }`}
            data-testid={`config-alqueire-${opt.id}`}>
            <p className="text-white font-medium text-sm">{opt.label}</p>
            <p className="text-yellow-500 text-xs mt-1">{opt.sub}</p>
            <p className="text-gray-500 text-xs mt-0.5">{opt.region}</p>
          </button>
        ))}
      </div>

      <div className="flex items-end gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Fator (hectares por alqueire)</label>
          <input type="number" step="0.01" value={form.fator_conversao}
            onChange={e => setForm({ ...form, fator_conversao: parseFloat(e.target.value) || 0 })}
            disabled={form.tipo_alqueire !== 'personalizado'}
            className="w-40 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm disabled:opacity-50"
            data-testid="config-fator" />
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 text-black font-semibold rounded-lg text-sm flex items-center gap-2"
          data-testid="config-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Settings size={16} />} Salvar Configuração
        </button>
      </div>
    </div>
  );
}
